import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Box, Button, CircularProgress, Grid, MenuItem, Paper, Stack, TextField, Typography } from "@mui/material";
import { Autocomplete } from "@mui/material";
import Modal from "./Modal";
import { createValidator, compose, required, maxLength, isDirty as dirty } from "../../utils/validationUtils";
import useFetchAspects from "../../hooks/useFetchAspects";
import useFetchQuestionKinds from "../../hooks/useFetchQuestionKinds";
import useFetchAnswerTypesByKind from "../../hooks/useFetchAnswerTypesByKind";

const emptyForm = {
  question_code: "",
  statement: "",
  kind: "single_choice",
  aspect_external_id: "",
  answer_type_external_id: "",
  metadataText: "",
};

const EditQuestionModal = ({ open = false, onClose, initialData, onSubmit, loading = false, onError }) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const { data: aspects = [], loading: aspectsLoading, error: aspectsError, refetch: refetchAspects } = useFetchAspects();
  const prevOpenRef = useRef(false);
  const { data: kinds = [], loading: kindsLoading, error: kindsError } = useFetchQuestionKinds();
  const skipAnswerTypes = !open || !form.kind || form.kind === "context";

  const {
    data: answerTypesByKind = [],
    loading: atByKindLoading,
    error: atByKindError,
  } = useFetchAnswerTypesByKind(form.kind, { skip: skipAnswerTypes });

  // Autocomplete
  const [aspectOption, setAspectOption] = useState(null);
  const [aspectInput, setAspectInput] = useState("");
  const [answerTypeOption, setAnswerTypeOption] = useState(null);
  const [answerTypeInput, setAnswerTypeInput] = useState("");

  // Normalizaciones
  const aspectOptions = useMemo(
    () => (aspects || []).map((a) => ({ external_id: a.external_id ?? "", name: a.name ?? "" })).filter((a) => a.external_id),
    [aspects],
  );

  const answerTypeOptions = useMemo(
    () => (answerTypesByKind || []).map((t) => ({ external_id: t.external_id ?? "", name: t.name ?? "" })).filter((t) => t.external_id),
    [answerTypesByKind],
  );

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      refetchAspects();
    }
    prevOpenRef.current = open;
  }, [open, refetchAspects]);

  useEffect(() => {
    if (!open) return;

    const meta = (() => {
      try {
        return initialData?.metadata ? JSON.stringify(initialData.metadata, null, 2) : "";
      } catch {
        return String(initialData?.metadata ?? "");
      }
    })();

    const initialFormState = {
      question_code: initialData?.question_code ?? "",
      statement: initialData?.statement ?? "",
      kind: initialData?.kind ?? "single_choice",
      aspect_external_id: initialData?.aspect_external_id ?? "",
      answer_type_external_id: initialData?.answer_type_external_id ?? "",
      metadataText: meta,
    };

    setForm(initialFormState);
    setErrors({});
    setTouched({});
    setSubmitting(false);

    const preAspect = initialFormState.aspect_external_id
      ? aspectOptions.find((o) => o.external_id === initialFormState.aspect_external_id) || null
      : null;

    setAspectOption(preAspect);
    setAspectInput(preAspect ? preAspect.external_id : "");
  }, [open, initialData, aspectOptions]);

  // Validación
  const validator = useMemo(() => {
    return createValidator({
      question_code: compose(required("El código es obligatorio."), maxLength(120)),
      statement: compose(required("El enunciado es obligatorio."), maxLength(1000)),
      kind: required("El tipo de pregunta es obligatorio."),
    });
  }, []);

  const validate = useCallback(
    (state) => {
      const base = validator(state);

      if (state.kind !== "context") {
        if (!state.aspect_external_id) base.aspect_external_id = "Seleccione un aspecto.";

        if (state.kind !== "open_text" && !state.answer_type_external_id) {
          base.answer_type_external_id = "Seleccione un tipo de respuesta.";
        }
      }

      if (state.metadataText?.trim()) {
        try {
          JSON.parse(state.metadataText);
        } catch {
          base.metadataText = "JSON inválido.";
        }
      }
      return base;
    },
    [validator],
  );

  const fieldError = (name) => (touched[name] ? errors[name] : undefined);
  const hasError = (name) => Boolean(fieldError(name));

  const hasAspectUiError = touched.aspect_external_id && !!errors.aspect_external_id && !form.aspect_external_id && !aspectOption;

  const hasAnswerTypeUiError =
    touched.answer_type_external_id && !!errors.answer_type_external_id && !form.answer_type_external_id && !answerTypeOption;

  const isDirty = useMemo(() => {
    const init = {
      question_code: initialData?.question_code ?? "",
      statement: initialData?.statement ?? "",
      kind: initialData?.kind ?? "single_choice",
      aspect_external_id: initialData?.aspect_external_id ?? "",
      answer_type_external_id: initialData?.answer_type_external_id ?? "",
      metadataText: (() => {
        try {
          return initialData?.metadata ? JSON.stringify(initialData.metadata, null, 2) : "";
        } catch {
          return String(initialData?.metadata ?? "");
        }
      })(),
    };
    return dirty(init, form);
  }, [form, initialData]);

  // Manejo de errores de hooks
  useEffect(() => {
    if (!open) return;
    const err = aspectsError?.message || kindsError?.message || atByKindError?.message || null;
    if (err && typeof onError === "function") {
      onError(err);
    }
  }, [open, aspectsError, kindsError, atByKindError, onError]);

  useEffect(() => {
    if (!open) return;

    if (form.answer_type_external_id) {
      const match = answerTypeOptions.find((o) => o.external_id === form.answer_type_external_id) || null;
      setAnswerTypeOption(match);
      setAnswerTypeInput(match ? match.external_id : "");
      if (match && (touched.answer_type_external_id || touched.kind)) {
        setErrors(() => validate(form));
      }
      return;
    }

    setAnswerTypeOption(null);
    setAnswerTypeInput("");
  }, [open, answerTypeOptions, form.answer_type_external_id, touched.answer_type_external_id, touched.kind, validate, form]);

  useEffect(() => {
    if (!open) return;

    if (form.aspect_external_id) {
      const match = aspectOptions.find((o) => o.external_id === form.aspect_external_id) || null;
      setAspectOption(match);
      setAspectInput(match ? match.external_id : "");
      if (match && touched.aspect_external_id) {
        setErrors(() => validate(form));
      }
      return;
    }

    setAspectOption(null);
    setAspectInput("");
  }, [open, aspectOptions, form.aspect_external_id, touched.aspect_external_id, validate, form]);

  // Handlers
  const handleChange = (field) => (ev) => {
    const value = ev.target.value;
    const next = { ...form, [field]: value };

    if (field === "kind") {
      if (value === "context") {
        next.aspect_external_id = "";
        next.answer_type_external_id = "";
        setAspectOption(null);
        setAspectInput("");
        setAnswerTypeOption(null);
        setAnswerTypeInput("");
      } else if (value === "open_text") {
        next.answer_type_external_id = "texto";
        setAnswerTypeOption(null);
        setAnswerTypeInput("texto");
      } else {
        next.answer_type_external_id = "";
        setAnswerTypeOption(null);
        setAnswerTypeInput("");
      }
    }

    setForm(next);
    setTouched((t) => ({ ...t, [field]: true }));
    setErrors(validate(next));
  };

  const handleBlur = (field) => () => {
    setTouched((t) => ({ ...t, [field]: true }));
    setErrors(validate(form));
  };

  const handleSubmit = async (ev) => {
    ev?.preventDefault();
    const v = validate(form);
    setErrors(v);
    setTouched({
      question_code: true,
      statement: true,
      kind: true,
      aspect_external_id: true,
      answer_type_external_id: true,
      metadataText: true,
    });
    if (Object.keys(v).length) return;

    const payload = {
      question_code: form.question_code.trim(),
      statement: form.statement.trim(),
      kind: form.kind,
      aspect_external_id: form.kind === "context" ? null : form.aspect_external_id || null,
      answer_type_external_id: form.kind === "context" ? null : form.kind === "open_text" ? "texto" : form.answer_type_external_id || null,
      metadata: (() => {
        if (!form.metadataText?.trim()) return null;
        try {
          return JSON.parse(form.metadataText);
        } catch {
          return null;
        }
      })(),
    };

    setSubmitting(true);
    let ok = false;
    try {
      ok = await onSubmit?.(initialData?.id, payload);
    } catch (e) {
      ok = false;
    }
    setSubmitting(false);
    if (ok) onClose?.();
  };

  const isSaving = submitting || loading;
  const disableRefs = form.kind === "context";
  const disableAnswerType = disableRefs || form.kind === "open_text";

  const autoLoading = aspectsLoading || atByKindLoading || kindsLoading;
  const autoError = !!(aspectsError || atByKindError || kindsError);

  return (
    <Modal open={open} handleClose={onClose} width="720px">
      <Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <form key={initialData?.id || "new"} onSubmit={handleSubmit} noValidate>
          <Stack gap={3}>
            {/* Encabezado */}
            <Box sx={{ p: 3 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 1.5 }}>
                Editar pregunta
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {initialData?.id && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(0,0,0,0.7)",
                      backgroundColor: "rgba(0,0,0,0.08)",
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: "0.8rem",
                      fontWeight: 500,
                    }}
                  >
                    {`ID pregunta: ${initialData.id}`}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Contenido */}
            <Box sx={{ p: 3, pt: 0 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Código"
                    value={form.question_code}
                    onChange={handleChange("question_code")}
                    onBlur={handleBlur("question_code")}
                    error={hasError("question_code")}
                    helperText={fieldError("question_code") || "Identificador externo de la pregunta."}
                    inputProps={{ maxLength: 120 }}
                    required
                    fullWidth
                  />
                </Grid>

                {/* Tipo de pregunta */}
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Tipo de pregunta"
                    value={form.kind}
                    onChange={handleChange("kind")}
                    onBlur={handleBlur("kind")}
                    error={hasError("kind")}
                    helperText={kindsError ? "No se pudieron cargar los tipos de pregunta." : fieldError("kind") || "Seleccione el tipo de pregunta."}
                    required
                    fullWidth
                    disabled={kindsLoading}
                  >
                    {(kinds || []).map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Enunciado"
                    value={form.statement}
                    onChange={handleChange("statement")}
                    onBlur={handleBlur("statement")}
                    error={hasError("statement")}
                    helperText={fieldError("statement") || "Texto visible para el usuario."}
                    inputProps={{ maxLength: 1000 }}
                    required
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={4}
                    sx={{
                      "& .MuiInputBase-inputMultiline": {
                        overflow: "auto",
                      },
                    }}
                  />
                </Grid>

                {/* Aspecto (ID externo) */}
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    value={aspectOption}
                    onChange={(_, newValue) => {
                      setAspectOption(newValue);
                      const external_id = newValue?.external_id || "";
                      const next = { ...form, aspect_external_id: external_id };
                      setForm(next);
                      setTouched((t) => ({ ...t, aspect_external_id: true }));
                      setErrors(validate(next));
                    }}
                    inputValue={aspectInput}
                    onInputChange={(_, newInputValue) => setAspectInput(newInputValue)}
                    options={aspectOptions}
                    getOptionLabel={(opt) => (opt ? opt.external_id : "")}
                    isOptionEqualToValue={(opt, v) => opt.external_id === v?.external_id}
                    loading={autoLoading}
                    disabled={disableRefs || autoLoading || autoError}
                    fullWidth
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Aspecto (ID externo)"
                        size="small"
                        onBlur={handleBlur("aspect_external_id")}
                        error={hasAspectUiError}
                        helperText={
                          disableRefs
                            ? "No aplica para preguntas de tipo 'Contexto'."
                            : hasAspectUiError
                              ? "Seleccione un aspecto."
                              : "Escriba para buscar…"
                        }
                        required={!disableRefs}
                      />
                    )}
                    openOnFocus
                    clearOnBlur={false}
                  />
                </Grid>

                {/* Tipo de respuesta */}
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    value={answerTypeOption}
                    onChange={(_, newValue) => {
                      setAnswerTypeOption(newValue);
                      const external_id = newValue?.external_id || "";
                      const next = { ...form, answer_type_external_id: external_id };
                      setForm(next);
                      setTouched((t) => ({ ...t, answer_type_external_id: true }));
                      setErrors(validate(next));
                    }}
                    inputValue={answerTypeInput}
                    onInputChange={(_, newInputValue) => setAnswerTypeInput(newInputValue)}
                    options={answerTypeOptions}
                    getOptionLabel={(opt) => (opt ? opt.external_id : "")}
                    isOptionEqualToValue={(opt, v) => opt.external_id === v?.external_id}
                    loading={atByKindLoading}
                    disabled={disableAnswerType || atByKindLoading || !!atByKindError}
                    fullWidth
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tipo de respuesta (ID externo)"
                        size="small"
                        onBlur={handleBlur("answer_type_external_id")}
                        error={hasAnswerTypeUiError}
                        helperText={
                          disableRefs
                            ? "No aplica para preguntas de tipo 'Contexto'."
                            : form.kind === "open_text"
                              ? "Se asigna automáticamente a 'texto'."
                              : atByKindError
                                ? "No se pudieron cargar los tipos de respuesta."
                                : hasAnswerTypeUiError
                                  ? "Seleccione un tipo de respuesta."
                                  : "Escriba para buscar…"
                        }
                        required={!disableRefs && form.kind !== "open_text"}
                      />
                    )}
                    openOnFocus
                    clearOnBlur={false}
                  />
                </Grid>

                {/* Metadata (JSON) */}
                <Grid item xs={12}>
                  <TextField
                    label="Metadata (JSON)"
                    value={form.metadataText}
                    onChange={handleChange("metadataText")}
                    onBlur={handleBlur("metadataText")}
                    error={hasError("metadataText")}
                    helperText={fieldError("metadataText") || "Opcional. Debe ser JSON válido."}
                    fullWidth
                    multiline
                    minRows={4}
                    maxRows={4}
                    sx={{
                      "& .MuiInputBase-inputMultiline": {
                        overflow: "auto",
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                        fontSize: "0.9rem",
                      },
                    }}
                    placeholder='{"placeholder": "Escriba su respuesta..."}'
                  />
                </Grid>
              </Grid>

              {/* Acciones */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, pt: 3, mt: 2 }}>
                <Button variant="outlined" onClick={onClose} disabled={isSaving} sx={{ borderRadius: 1, px: 3 }}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSaving || !isDirty || Object.keys(validate(form)).length > 0}
                  sx={{ minWidth: 140, borderRadius: 1, px: 3 }}
                >
                  {isSaving ? (
                    <Stack direction="row" alignItems="center" gap={1}>
                      <CircularProgress size={16} />
                      <span>Actualizando…</span>
                    </Stack>
                  ) : (
                    "Actualizar"
                  )}
                </Button>
              </Box>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Modal>
  );
};

export default EditQuestionModal;
